import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    NavigateBefore,
    NavigateNext,
    Today,
    ExpandMore
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/es';
import axios from 'axios';
import { UrlApi } from '../../services/apirest';
import Navbar from '../Navbar';

moment.locale('es');

const CanjeResumenPorEmpresa = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [fechaSeleccionada, setFechaSeleccionada] = useState(moment());
    const [resumen, setResumen] = useState(null);
    const [tiposEnvase, setTiposEnvase] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [expandedEmpresas, setExpandedEmpresas] = useState({});
    const [todosExpandidos, setTodosExpandidos] = useState(false);

    useEffect(() => {
        cargarDatosIniciales();
    }, []);

    useEffect(() => {
        if (tiposEnvase.length > 0) {
            cargarResumen();
        }
    }, [fechaSeleccionada, tiposEnvase]);

    const cargarDatosIniciales = async () => {
        try {
            const [tiposRes, empresasRes] = await Promise.all([
                axios.get(UrlApi + '/canjes_routes/canje_tipos_envase'),
                axios.get(UrlApi + '/canjes_routes/canje_empresas')
            ]);

            setTiposEnvase(tiposRes.data);
            setEmpresas(empresasRes.data);
        } catch (err) {
            console.error('Error al cargar datos iniciales:', err);
            setError('Error al cargar datos iniciales');
        }
    };

    const cargarResumen = async () => {
        try {
            setLoading(true);
            const fechaFormateada = fechaSeleccionada.format('YYYY-MM-DD');
            const response = await axios.get(`${UrlApi}/canjes_routes/canje_stock/resumen/${fechaFormateada}`);
            setResumen(response.data);
            setError(null);
        } catch (err) {
            console.error('Error al cargar resumen:', err);
            setError('Error al cargar el resumen');
        } finally {
            setLoading(false);
        }
    };

    const handleDiaAnterior = () => {
        setFechaSeleccionada(moment(fechaSeleccionada).subtract(1, 'days'));
    };

    const handleDiaSiguiente = () => {
        setFechaSeleccionada(moment(fechaSeleccionada).add(1, 'days'));
    };

    const handleHoy = () => {
        setFechaSeleccionada(moment());
    };

    const procesarDatosPorEmpresa = () => {
        if (!resumen || !resumen.sucursales || !empresas.length) return [];

        const datosPorEmpresa = [];

        empresas.forEach(empresa => {
            const datosEmpresa = {
                empresa_id: empresa.id,
                nombre_empresa: empresa.nombre_empresa,
                sucursales: [],
                totales: {}
            };

            // Inicializar totales
            tiposEnvase.forEach(tipo => {
                datosEmpresa.totales[tipo.capacidad_kg] = 0;
            });

            // Recorrer cada sucursal
            resumen.sucursales.forEach(sucursal => {
                const empresaEnSucursal = sucursal.empresas[empresa.id];

                if (empresaEnSucursal) {
                    // Verificar si tiene alguna cantidad > 0
                    const tieneCantidad = Object.values(empresaEnSucursal.envases).some(cant => cant > 0);

                    if (tieneCantidad) {
                        datosEmpresa.sucursales.push({
                            sucursal_id: sucursal.sucursal_id,
                            sucursal: sucursal.sucursal,
                            envases: empresaEnSucursal.envases
                        });

                        // Acumular totales
                        Object.entries(empresaEnSucursal.envases).forEach(([capacidad, cantidad]) => {
                            datosEmpresa.totales[capacidad] = (datosEmpresa.totales[capacidad] || 0) + cantidad;
                        });
                    }
                }
            });

            // Solo agregar la empresa si tiene sucursales con datos
            if (datosEmpresa.sucursales.length > 0) {
                datosPorEmpresa.push(datosEmpresa);
            }
        });

        return datosPorEmpresa;
    };

    const datosPorEmpresa = procesarDatosPorEmpresa();

    const handleExpandirTodos = () => {
        const nuevoEstado = {};
        datosPorEmpresa.forEach(empresa => {
            nuevoEstado[empresa.empresa_id] = true;
        });
        setExpandedEmpresas(nuevoEstado);
        setTodosExpandidos(true);
    };

    const handleColapsarTodos = () => {
        setExpandedEmpresas({});
        setTodosExpandidos(false);
    };

    const handleToggleEmpresa = (empresaId) => {
        setExpandedEmpresas(prev => ({
            ...prev,
            [empresaId]: !prev[empresaId]
        }));
    };

    return (
        <>
            <Navbar />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                    {/* Header FIJO con selector de fecha */}
                    <Paper
                        sx={{
                            p: isMobile ? 1.5 : 2,
                            position: isMobile ? 'fixed' : 'sticky',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1100,
                            boxShadow: 3
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection={isMobile ? 'column' : 'row'}
                            justifyContent="space-between"
                            alignItems={isMobile ? 'stretch' : 'center'}
                            pt={isMobile ? '50px' : 3}
                            gap={isMobile ? 1.5 : 0}
                        >
                            <Typography
                                variant={isMobile ? 'h6' : 'h5'}
                                component="h2"
                                sx={{ fontSize: isMobile ? '1rem' : '1.5rem' }}
                            >
                                Canje-Resumen Envases x Empresa
                            </Typography>

                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent={isMobile ? 'center' : 'flex-end'}
                                gap={0.5}
                            >
                                <IconButton onClick={handleDiaAnterior} color="primary" size={isMobile ? 'small' : 'medium'}>
                                    <NavigateBefore />
                                </IconButton>

                                <DatePicker
                                    label="Fecha"
                                    value={fechaSeleccionada}
                                    onChange={(newValue) => setFechaSeleccionada(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: { width: isMobile ? '120px' : '150px' }
                                        }
                                    }}
                                />

                                <IconButton onClick={handleDiaSiguiente} color="primary" size={isMobile ? 'small' : 'medium'}>
                                    <NavigateNext />
                                </IconButton>

                                <Button
                                    variant="outlined"
                                    startIcon={!isMobile && <Today />}
                                    onClick={handleHoy}
                                    size="small"
                                    sx={{ minWidth: isMobile ? '50px' : 'auto' }}
                                >
                                    HOY
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={todosExpandidos ? handleColapsarTodos : handleExpandirTodos}
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    {todosExpandidos ? 'Colapsar Todo' : 'Expandir Todo'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Contenido con SCROLL */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: isMobile ? 1 : 3, pt: isMobile ? '130px' : 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <Box display="flex" justifyContent="center" my={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {datosPorEmpresa.length > 0 ? (
                                    <Box>
                                        {datosPorEmpresa.map(empresa => (
                                            <Accordion
                                                key={empresa.empresa_id}
                                                expanded={expandedEmpresas[empresa.empresa_id] || false}
                                                onChange={() => handleToggleEmpresa(empresa.empresa_id)}
                                                sx={{ mb: isMobile ? 1.5 : 2, boxShadow: 3 }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    sx={{
                                                        backgroundColor: '#1976d2',
                                                        color: 'white',
                                                        minHeight: isMobile ? '48px' : '56px',
                                                        '& .MuiAccordionSummary-content': {
                                                            margin: isMobile ? '8px 0' : '12px 0'
                                                        },
                                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        flexDirection={isMobile ? 'column' : 'row'}
                                                        alignItems={isMobile ? 'flex-start' : 'center'}
                                                        gap={isMobile ? 0.5 : 1.5}
                                                        width="100%"
                                                    >
                                                        <Typography
                                                            variant={isMobile ? 'subtitle1' : 'h6'}
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: isMobile ? '0.95rem' : '1.1rem'
                                                            }}
                                                        >
                                                            {empresa.nombre_empresa}
                                                        </Typography>
                                                        <Box
                                                            display="flex"
                                                            flexWrap="wrap"
                                                            gap={0.5}
                                                        >
                                                            {tiposEnvase.map(tipo => {
                                                                const cantidad = empresa.totales[tipo.capacidad_kg] || 0;
                                                                if (cantidad === 0) return null;

                                                                return (
                                                                    <Chip
                                                                        key={tipo.id}
                                                                        label={`${tipo.capacidad_kg}kg: ${cantidad}`}
                                                                        size={isMobile ? 'small' : 'medium'}
                                                                        sx={{
                                                                            backgroundColor: 'white',
                                                                            color: '#1976d2',
                                                                            fontWeight: 600,
                                                                            fontSize: isMobile ? '0.7rem' : '0.8125rem',
                                                                            height: isMobile ? '20px' : '24px'
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ p: isMobile ? 1.5 : 3 }}>
                                                    <TableContainer
                                                        component={Paper}
                                                        variant="outlined"
                                                        sx={{ overflowX: 'auto' }}
                                                    >
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            backgroundColor: '#f5f5f5',
                                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                            padding: isMobile ? '4px' : '6px 8px',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        Sucursal
                                                                    </TableCell>
                                                                    {tiposEnvase.map(tipo => (
                                                                        <TableCell
                                                                            key={tipo.id}
                                                                            align="right"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                backgroundColor: '#f5f5f5',
                                                                                fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                padding: isMobile ? '4px' : '6px 8px',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {tipo.capacidad_kg}kg
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {empresa.sucursales.map(sucursal => (
                                                                    <TableRow key={sucursal.sucursal_id} hover>
                                                                        <TableCell
                                                                            sx={{
                                                                                fontWeight: 500,
                                                                                fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                padding: isMobile ? '4px' : '6px 8px',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {sucursal.sucursal}
                                                                        </TableCell>
                                                                        {tiposEnvase.map(tipo => (
                                                                            <TableCell
                                                                                key={tipo.id}
                                                                                align="right"
                                                                                sx={{
                                                                                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                                    padding: isMobile ? '4px' : '6px 8px'
                                                                                }}
                                                                            >
                                                                                {sucursal.envases[tipo.capacidad_kg] || 0}
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                ))}

                                                                {/* Fila de totales */}
                                                                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                                    <TableCell
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                                            padding: isMobile ? '4px' : '6px 8px',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        TOTAL
                                                                    </TableCell>
                                                                    {tiposEnvase.map(tipo => (
                                                                        <TableCell
                                                                            key={tipo.id}
                                                                            align="right"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                fontSize: isMobile ? '0.8rem' : '1rem',
                                                                                padding: isMobile ? '4px' : '6px 8px'
                                                                            }}
                                                                        >
                                                                            {empresa.totales[tipo.capacidad_kg] || 0}
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        No hay datos registrados para esta fecha
                                    </Alert>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </LocalizationProvider>
        </>
    );
};

export default CanjeResumenPorEmpresa;